import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { adminStatsService } from '../../../services/adminStatsService';
import { scheduleService } from '../../../services/scheduleService';
import { standingsService } from '../../../services/standingsService';
import { useSeasonContext } from '../../../context/SeasonContext';
import '../../../styles/MaxPrepsBlock.css';

const MaxPrepsItemBlock = ({ block, onUpdate, onDelete }) => {
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [selectedGameId, setSelectedGameId] = useState('');
  const [weeks, setWeeks] = useState([]);
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameScore, setGameScore] = useState(null);
  const [teamStats, setTeamStats] = useState({ team1: null, team2: null });
  const [topPerformers, setTopPerformers] = useState({});
  const [allStats, setAllStats] = useState({});
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStatCategory, setSelectedStatCategory] = useState('PASSING');
  
  const { selectedSeason } = useSeasonContext();

  useEffect(() => {
    if (block?.gameData && block?.teamStats) {
      const reconstructedGame = {
        id: block.gameId,
        gameId: block.gameId,
        team1: block.gameData.team1?.name || block.gameData.team1,
        team2: block.gameData.team2?.name || block.gameData.team2,
        team1_id: block.gameData.team1?.id,
        team2_id: block.gameData.team2?.id,
        week: block.gameData.week,
        scheduled_date: block.gameData.date,
        game_scores: block.gameScore ? [block.gameScore] : []
      };
      setSelectedGame(reconstructedGame);
      setTeamStats(block.teamStats);
      setGameScore(block.gameScore);
      setTopPerformers(block.topPerformers || {});
      if (block.allStats) {
        setAllStats(block.allStats);
      }
      if (block.gameData?.week) {
        setSelectedWeek(block.gameData.week);
      }
      if (block.gameId) {
        setSelectedGameId(block.gameId.toString());
      }
    }
  }, [block?.id, block?.gameId]);

  useEffect(() => {
    if (selectedSeason?.id) {
      fetchScheduleData();
    }
  }, [selectedSeason?.id]);

  useEffect(() => {
    if (selectedSeason?.id && selectedWeek) {
      fetchGamesForWeek();
    }
  }, [selectedSeason?.id, selectedWeek]);

  useEffect(() => {
    if (selectedGameId && games.length > 0) {
      const game = games.find(g => g.id === parseInt(selectedGameId));
      if (game) {
        console.log('[MaxPrepsItemBlock] Selected game object:', { gameId: game.id, team1: game.team1, team2: game.team2, team1_id: game.team1_id, team2_id: game.team2_id, allKeys: Object.keys(game) });
        setSelectedGame(game);
        fetchGameDetails(game);
      }
    }
  }, [selectedGameId, games]);

  useEffect(() => {
    if (selectedGame && teamStats?.team1) {
      const scoreStr = gameScore 
        ? `${gameScore.team1_score} - ${gameScore.team2_score}`
        : '';
      
      const updateData = {
        gameId: selectedGame.id || selectedGame.gameId,
        gameData: {
          team1: {
            name: typeof selectedGame.team1 === 'object' ? selectedGame.team1?.name : selectedGame.team1,
            id: selectedGame.team1_id
          },
          team2: {
            name: typeof selectedGame.team2 === 'object' ? selectedGame.team2?.name : selectedGame.team2,
            id: selectedGame.team2_id
          },
          week: selectedGame.week,
          date: selectedGame.scheduled_date
        },
        gameScore: gameScore,
        finalScore: scoreStr,
        teamStats,
        topPerformers,
        allStats
      };
      
      onUpdate(block.id, updateData);
    }
  }, [selectedGame?.id, selectedGame?.team1_id, selectedGame?.team2_id, gameScore?.id, block.id]);

  const fetchScheduleData = async () => {
    try {
      const schedule = await scheduleService.getScheduleBySeason(selectedSeason.id);
      let uniqueWeeks = [];
      if (schedule && schedule.length > 0) {
        uniqueWeeks = [...new Set(schedule.map(g => g.week))].sort((a, b) => a - b);
      } else {
        uniqueWeeks = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
      }
      setWeeks(uniqueWeeks);
      setSelectedWeek(uniqueWeeks[0] || 1);
      
      const statTypes = ['PASSING', 'RUSHING', 'RECEIVING', 'DEFENSE', 'KICKING', 'BLOCKING'];
      const stats = {};
      for (const type of statTypes) {
        try {
          stats[type] = await adminStatsService.getAllSeasonStats(type, selectedSeason.id);
        } catch (error) {
          console.error(`Error fetching ${type} stats:`, error);
          stats[type] = [];
        }
      }
      setAllStats(stats);
      
      const standings_data = await standingsService.getStandingsBySeason(selectedSeason.id);
      setStandings(standings_data);
    } catch (error) {
      console.error('Error fetching schedule data:', error);
      setWeeks([1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);
    }
  };

  const fetchGamesForWeek = async () => {
    try {
      setLoading(true);
      const weekGames = await scheduleService.getGamesByWeek(selectedSeason.id, selectedWeek);
      setGames(weekGames);
      setSelectedGameId('');
      setSelectedGame(null);
      setGameScore(null);
      setTopPerformers({});
    } catch (error) {
      console.error('Error fetching games for week:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGameDetails = async (game) => {
    try {
      setLoading(true);
      
      const team1Standing = standings.find(s => s.team_id === game.team1_id);
      const team2Standing = standings.find(s => s.team_id === game.team2_id);
      
      setTeamStats({
        team1: {
          name: game.team1?.name,
          abbreviation: game.team1?.abbreviation,
          stats: team1Standing,
        },
        team2: {
          name: game.team2?.name,
          abbreviation: game.team2?.abbreviation,
          stats: team2Standing,
        }
      });

      setGameScore(game.game_scores?.[0] || null);

      const team1Name = typeof game.team1 === 'object' ? game.team1?.name : game.team1;
      const team2Name = typeof game.team2 === 'object' ? game.team2?.name : game.team2;

      const performers = {};
      const statCategories = ['PASSING', 'RUSHING', 'RECEIVING', 'DEFENSE', 'KICKING', 'BLOCKING'];
      
      for (const category of statCategories) {
        const categoryStats = allStats[category] || [];
        const team1Players = categoryStats.filter(p => 
          p.TEAM === team1Name || 
          p.team === team1Name ||
          (p.TEAM && team1Name && p.TEAM.toLowerCase().trim() === team1Name.toLowerCase().trim()) ||
          (p.team && team1Name && p.team.toLowerCase().trim() === team1Name.toLowerCase().trim())
        );
        const team2Players = categoryStats.filter(p => 
          p.TEAM === team2Name || 
          p.team === team2Name ||
          (p.TEAM && team2Name && p.TEAM.toLowerCase().trim() === team2Name.toLowerCase().trim()) ||
          (p.team && team2Name && p.team.toLowerCase().trim() === team2Name.toLowerCase().trim())
        );
        
        performers[category] = {
          team1: getTopPerformer(team1Players, category),
          team2: getTopPerformer(team2Players, category),
        };
      }
      
      setTopPerformers(performers);
    } catch (error) {
      console.error('Error fetching game details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTopPerformer = (players, category) => {
    if (!players || players.length === 0) return null;

    const statColumns = {
      'PASSING': 'YRDS',
      'RUSHING': 'YRDS',
      'RECEIVING': 'YRDS',
      'DEFENSE': 'TCK',
      'KICKING': 'PNTS',
      'BLOCKING': 'SNPS',
    };

    const column = statColumns[category];
    let topPlayer = null;
    let maxValue = -Infinity;

    for (const player of players) {
      const value = parseFloat(player[column]) || 0;
      if (value > maxValue) {
        maxValue = value;
        topPlayer = player;
      }
    }

    return topPlayer;
  };

  return (
    <div className="block-item maxpreps-block">
      <div className="block-header">
        <span>MaxPreps Game Details</span>
        <button
          type="button"
          className="btn-icon-small delete"
          onClick={() => onDelete(block.id)}
        >
          <Trash2 size={16} />
        </button>
      </div>

      <div className="maxpreps-selector">
        <div className="selector-group">
          <label>Week:</label>
          <select
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
            className="field-select"
          >
            {weeks.map((week) => (
              <option key={week} value={week}>
                Week {week}
              </option>
            ))}
          </select>
        </div>

        <div className="selector-group">
          <label>Game:</label>
          <select
            value={selectedGameId}
            onChange={(e) => setSelectedGameId(e.target.value)}
            className="field-select"
            disabled={loading || games.length === 0}
          >
            <option value="">{games.length === 0 ? 'No games this week' : 'Select a game'}</option>
            {games.map((game) => {
              const score = game.game_scores?.[0];
              const display = score
                ? `${game.team1?.abbreviation || game.team1?.name} ${score.team1_score} - ${score.team2_score} ${game.team2?.abbreviation || game.team2?.name}`
                : `${game.team1?.name} vs ${game.team2?.name}`;
              return (
                <option key={game.id} value={game.id}>
                  {display}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {games.length === 0 && (
        <div className="empty-state" style={{ padding: '1rem', textAlign: 'center', color: '#999' }}>
          No games scheduled for Week {selectedWeek}. Select a different week or create a schedule.
        </div>
      )}

      {selectedGame && (
        <div className="maxpreps-display">
          <div className="game-score-section">
            <h4>Final Score</h4>
            {gameScore ? (
              <div className="score-display">
                <div className="team-score">
                  <div className="team-name">{selectedGame.team1?.name}</div>
                  <div className="score">{gameScore.team1_score}</div>
                </div>
                <div className="vs">vs</div>
                <div className="team-score">
                  <div className="team-name">{selectedGame.team2?.name}</div>
                  <div className="score">{gameScore.team2_score}</div>
                </div>
              </div>
            ) : (
              <p>Score not available</p>
            )}
          </div>

          <div className="team-stats-section">
            <h4>Team Stats</h4>
            <div className="team-stats-grid">
              {teamStats.team1?.stats && (
                <div className="team-stats-card">
                  <h5>{teamStats.team1.name}</h5>
                  <div className="stat-row">
                    <span>Record:</span>
                    <strong>{teamStats.team1.stats.wins}W - {teamStats.team1.stats.losses}L</strong>
                  </div>
                  <div className="stat-row">
                    <span>Points For:</span>
                    <strong>{teamStats.team1.stats.points_for}</strong>
                  </div>
                  <div className="stat-row">
                    <span>Points Against:</span>
                    <strong>{teamStats.team1.stats.points_against}</strong>
                  </div>
                </div>
              )}
              {teamStats.team2?.stats && (
                <div className="team-stats-card">
                  <h5>{teamStats.team2.name}</h5>
                  <div className="stat-row">
                    <span>Record:</span>
                    <strong>{teamStats.team2.stats.wins}W - {teamStats.team2.stats.losses}L</strong>
                  </div>
                  <div className="stat-row">
                    <span>Points For:</span>
                    <strong>{teamStats.team2.stats.points_for}</strong>
                  </div>
                  <div className="stat-row">
                    <span>Points Against:</span>
                    <strong>{teamStats.team2.stats.points_against}</strong>
                  </div>
                </div>
              )}
            </div>

            <div className="team-comparison-stats">
              <div className="stat-category-tabs">
                {['PASSING', 'RUSHING', 'RECEIVING', 'DEFENSE', 'KICKING', 'BLOCKING'].map((category) => (
                  <button
                    key={category}
                    className={`tab-button ${selectedStatCategory === category ? 'active' : ''}`}
                    onClick={() => setSelectedStatCategory(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>
              
              {selectedGame && teamStats.team1 && teamStats.team2 && (
                <table className="team-comparison-table">
                  <thead>
                    <tr>
                      <th className="team-name-col">{teamStats.team1.name}</th>
                      <th className="stat-label-col">STAT</th>
                      <th className="team-name-col">{teamStats.team2.name}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allStats[selectedStatCategory]?.length > 0 ? (
                      Object.keys(allStats[selectedStatCategory]?.[0] || {})
                        .filter(key => !['id', 'TEAM_ID', 'TEAM', 'NAME', 'NO', 'POS'].includes(key))
                        .map((statKey) => {
                          const team1Players = allStats[selectedStatCategory]?.filter(p => p.TEAM === teamStats.team1.name) || [];
                          const team2Players = allStats[selectedStatCategory]?.filter(p => p.TEAM === teamStats.team2.name) || [];
                          
                          const team1Value = team1Players.length > 0 
                            ? (parseFloat(team1Players.reduce((sum, p) => sum + (parseFloat(p[statKey]) || 0), 0) / team1Players.length).toFixed(2))
                            : '-';
                          const team2Value = team2Players.length > 0
                            ? (parseFloat(team2Players.reduce((sum, p) => sum + (parseFloat(p[statKey]) || 0), 0) / team2Players.length).toFixed(2))
                            : '-';

                          return (
                            <tr key={statKey}>
                              <td className={`stat-value ${parseFloat(team1Value) > parseFloat(team2Value) ? 'winner' : ''}`}>
                                {team1Value}
                              </td>
                              <td className="stat-label-col">{statKey}</td>
                              <td className={`stat-value ${parseFloat(team2Value) > parseFloat(team1Value) ? 'winner' : ''}`}>
                                {team2Value}
                              </td>
                            </tr>
                          );
                        })
                    ) : (
                      <tr>
                        <td colSpan="3" style={{ textAlign: 'center', padding: '1rem' }}>
                          No stats available for this category
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="top-performers-section">
            <h4>Top Performers</h4>
            <div className="performers-grid">
              {Object.entries(topPerformers).map(([category, performers]) => (
                <div key={category} className="performer-category">
                  <h6>{category}</h6>
                  <div className="performer-matchup">
                    <div className="performer-card team1">
                      {performers.team1 ? (
                        <>
                          <div className="player-name">{performers.team1.NAME}</div>
                          <div className="player-stat">{performers.team1[getStatKey(category)]}</div>
                        </>
                      ) : (
                        <div className="no-player">-</div>
                      )}
                    </div>
                    <div className="performer-card team2">
                      {performers.team2 ? (
                        <>
                          <div className="player-name">{performers.team2.NAME}</div>
                          <div className="player-stat">{performers.team2[getStatKey(category)]}</div>
                        </>
                      ) : (
                        <div className="no-player">-</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const getStatKey = (category) => {
  const keys = {
    'PASSING': 'YRDS',
    'RUSHING': 'YRDS',
    'RECEIVING': 'YRDS',
    'DEFENSE': 'TCK',
    'KICKING': 'PNTS',
    'BLOCKING': 'SNPS',
  };
  return keys[category] || 'YRDS';
};

export default MaxPrepsItemBlock;
